/*=========================================================================================
  File Name: pageTitle.js
  Description: Builds and applies descriptive document titles per route.
               Addresses WCAG 2.2 SC 2.4.2 Page Titled (Level A) - a single-page app keeps
               the static <title> from index.html on every route unless it is set on
               navigation.
  ----------------------------------------------------------------------------------------
  Item Name: Virtual Job Experience
==========================================================================================*/

export const APP_NAME = 'Virtual Job Experience';

// me2's recommended format is "<Page> - Virtual Job Experience" (audit issue 30):
// front-loaded, hyphen separated, using the label the user already sees.
const SEPARATOR = ' - ';

/**
 * Collect the titles of every matched route record, most specific first.
 *
 * vue-router 3 does not merge `meta` down a nested route chain - `route.meta` is only the
 * meta of the deepest matched record - so the parent titles have to be gathered by walking
 * `route.matched` ourselves. This is what turns a bare "Activities" into the more
 * descriptive "Activities | Work experience".
 *
 * A `meta.title` may be a string, or a function receiving the route for titles that depend
 * on route params.
 *
 * @param {Object} route a vue-router route object
 * @returns {string[]}
 */
export function resolveRouteTitles(route) {
  if (!route || !Array.isArray(route.matched)) {
    return [];
  }

  const titles = [];

  for (let i = route.matched.length - 1; i >= 0; i -= 1) {
    const meta = route.matched[i].meta || {};
    const title = typeof meta.title === 'function' ? meta.title(route) : meta.title;

    // Skip blanks, and skip a child that repeats its parent's title.
    if (title && titles[titles.length - 1] !== title) {
      titles.push(title);
    }
  }

  return titles;
}

/**
 * @param {string|string[]} title
 * @returns {string} the title as it should appear in the browser tab
 */
export function formatPageTitle(title) {
  const parts = (Array.isArray(title) ? title : [title]).filter(Boolean);

  return parts.length ? `${parts.join(SEPARATOR)}${SEPARATOR}${APP_NAME}` : APP_NAME;
}

/**
 * Set the document title. Exported so that views rendering named content (an experience, an
 * activity) can replace the generic route title once that content has loaded.
 *
 * @param {string|string[]} title
 */
export function setPageTitle(title) {
  document.title = formatPageTitle(title);
}

/**
 * @param {Object} route a vue-router route object
 */
export function setPageTitleFromRoute(route) {
  setPageTitle(resolveRouteTitles(route));
}
